import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { api, fmtINR, STATUS_COLORS, STATUS_LABEL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, MagnifyingGlass, FunnelSimple } from "@phosphor-icons/react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import Pagination from "@/components/Pagination";
import SortableHeader from "@/components/SortableHeader";
import WhatsAppNudge from "@/components/WhatsAppNudge";

const STATUSES = ["submitted", "resubmitted", "approved", "paid", "rejected", "sent_back"];
const PAGE_SIZE = 10;

export default function Expenses() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState(null);
  const [loading, setLoading] = useState(true);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  // Reset to page 1 when filters change
  useEffect(() => { setPage(1); }, [status, debouncedQ]);

  useEffect(() => {
    setLoading(true);
    const params = { page, page_size: PAGE_SIZE };
    if (status !== "all") params.status = status;
    if (debouncedQ) params.q = debouncedQ;
    if (sortBy && sortDir) { params.sort_by = sortBy; params.sort_dir = sortDir; }
    api.get("/expenses", { params })
      .then((r) => { setItems(r.data.items || []); setTotal(r.data.total || 0); })
      .finally(() => setLoading(false));
  }, [status, debouncedQ, page, sortBy, sortDir]);

  const applySort = (field, dir) => {
    setSortBy(field); setSortDir(dir); setPage(1);
  };
  const hdrProps = { sortBy, sortDir, onChange: applySort };

  const canCreate = ["admin", "centre_manager"].includes(user?.role);

  const isPending = (s) => ["submitted", "resubmitted", "in_review"].includes(s);

  return (
    <div className="p-4 lg:p-8 space-y-6" data-testid="expenses-root">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-end gap-3">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">Ledger</div>
          <h1 className="font-heading text-3xl sm:text-4xl mt-1 tracking-tight">Expenses</h1>
        </div>
        {canCreate && (
          <Button onClick={() => nav("/expenses/new")} data-testid="new-expense-btn"
            className="rounded-sm bg-zinc-900 hover:bg-zinc-800 text-white h-10 gap-2 w-full sm:w-auto">
            <Plus size={14} weight="bold" /> New Expense
          </Button>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-3 sm:items-center bg-white border border-zinc-200 p-3">
        <div className="relative flex-1 sm:max-w-md">
          <MagnifyingGlass size={14} className="absolute left-3 top-3 text-zinc-400" />
          <Input placeholder="Search expense no, vendor, invoice…"
            value={q} onChange={(e) => setQ(e.target.value)}
            className="pl-9 rounded-sm h-9 border-zinc-300"
            data-testid="expense-search" />
        </div>
        <div className="flex items-center gap-2">
          <FunnelSimple size={14} className="text-zinc-500 shrink-0" />
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="rounded-sm h-9 w-full sm:w-44 border-zinc-300" data-testid="expense-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="sm:ml-auto sm:text-right pr-2" data-testid="expenses-total-strip">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Total records</div>
          <div className="font-heading text-lg tabular-nums" data-testid="expenses-count">{total}</div>
        </div>
      </div>

      {/* Desktop / tablet table */}
      <div className="bg-white border border-zinc-200 overflow-x-auto hidden md:block">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-[10px] uppercase tracking-widest text-zinc-500">
            <tr>
              <th className="text-left p-4"><SortableHeader field="expense_no" label="Expense No" {...hdrProps} /></th>
              <th className="text-left p-4"><SortableHeader field="expense_date" label="Date" {...hdrProps} /></th>
              <th className="text-left p-4">Centre</th>
              <th className="text-left p-4"><SortableHeader field="category" label="Category" {...hdrProps} /></th>
              <th className="text-left p-4"><SortableHeader field="vendor" label="Vendor" {...hdrProps} /></th>
              <th className="text-left p-4">Invoice</th>
              <th className="text-right p-4"><SortableHeader field="total_amount" label="Amount" align="right" {...hdrProps} /></th>
              <th className="text-left p-4"><SortableHeader field="status" label="Status" {...hdrProps} /></th>
              <th className="text-right p-4 w-14">Nudge</th>
            </tr>
          </thead>
          <tbody className="tabular-nums">
            {loading && <tr><td colSpan={9} className="p-8 text-center text-zinc-400">Loading…</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan={9} className="p-8 text-center text-zinc-400">No expenses found</td></tr>
            )}
            {!loading && items.map((e) => (
              <tr key={e.id} className="border-t border-zinc-100 hover:bg-zinc-50 cursor-pointer"
                onClick={() => nav(`/expenses/${e.id}`)}
                data-testid={`expense-row-${e.expense_no}`}>
                <td className="p-4 font-mono text-xs text-zinc-900">{e.expense_no}</td>
                <td className="p-4 text-zinc-600">{e.expense_date}</td>
                <td className="p-4"><div className="font-medium">{e.centre_code}</div><div className="text-xs text-zinc-500">{e.centre_name}</div></td>
                <td className="p-4">{e.category}</td>
                <td className="p-4 text-zinc-600">{e.vendor || "—"}</td>
                <td className="p-4 text-zinc-600 font-mono text-xs">{e.invoice_number || "—"}</td>
                <td className="p-4 text-right font-medium">{fmtINR(e.total_amount)}</td>
                <td className="p-4"><span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-widest border ${STATUS_COLORS[e.status]}`}>{STATUS_LABEL[e.status]}</span></td>
                <td className="p-2 text-right">
                  {isPending(e.status) && (
                    <WhatsAppNudge kind="expense" id={e.id} number={e.expense_no} variant="icon" />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pagination page={page} pageSize={PAGE_SIZE} total={total}
          onPageChange={setPage} testid="expenses-pagination" />
      </div>

      {/* Mobile card list */}
      <div className="md:hidden space-y-3" data-testid="expenses-mobile-list">
        {loading && <div className="p-8 text-center text-zinc-400 bg-white border border-zinc-200">Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="p-8 text-center text-zinc-400 bg-white border border-zinc-200">No expenses found</div>
        )}
        {!loading && items.map((e) => (
          <div
            key={e.id}
            onClick={() => nav(`/expenses/${e.id}`)}
            data-testid={`expense-card-${e.expense_no}`}
            className="bg-white border border-zinc-200 p-4 active:bg-zinc-50 cursor-pointer"
          >
            <div className="flex justify-between items-start gap-3">
              <div className="min-w-0 flex-1">
                <div className="font-mono text-xs text-zinc-500">{e.expense_no}</div>
                <div className="mt-1 text-sm font-medium truncate">{e.vendor || e.category || "—"}</div>
                <div className="text-xs text-zinc-500 mt-0.5 truncate">
                  {e.centre_code} · {e.expense_date}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-heading text-lg tabular-nums">{fmtINR(e.total_amount)}</div>
                <span className={`inline-block mt-1 px-2 py-0.5 text-[10px] uppercase tracking-widest border ${STATUS_COLORS[e.status]}`}>
                  {STATUS_LABEL[e.status]}
                </span>
              </div>
            </div>
            {isPending(e.status) && (
              <div className="mt-3 pt-3 border-t border-zinc-100 flex justify-end">
                <WhatsAppNudge kind="expense" id={e.id} number={e.expense_no} variant="inline" />
              </div>
            )}
          </div>
        ))}
        <div className="bg-white border border-zinc-200">
          <Pagination page={page} pageSize={PAGE_SIZE} total={total}
            onPageChange={setPage} testid="expenses-pagination-mobile" />
        </div>
      </div>
    </div>
  );
}
